import { router } from "@inertiajs/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Timeline } from "@/lib/therapist-relocation";
import type { ExternalProp } from "@/types/admin-portal/routing-mode";
import type { TherapistAddress } from "@/types/admin-portal/therapist";
import { AddressLines, AddressTimelineBlock } from "./address-steps";
import type { UpcomingVisit } from "./address-timeline";

/** The therapists index's optional `selectedTherapistTimeline` prop. */
export interface DetailsTimeline {
	therapistId: string;
	relocationSupported: boolean;
	timeline: ExternalProp<Timeline> | null;
	upcomingVisits: UpcomingVisit[];
}

/** What the panel's Alamat shows for the therapist it has open. */
export interface DetailsTimelineState {
	relocationSupported: boolean;
	/** undefined while it loads */
	timeline?: ExternalProp<Timeline>;
	upcomingVisits: UpcomingVisit[];
	retry: () => void;
}

/**
 * Asks for Go's timeline once per therapist opened. The panel calls it, not DetailsAddressSection:
 * the Alamat accordion mounts its content only when opened, and the timeline should be there by then.
 * A reload that brings no answer (network, 5xx) counts as failed, so the spinner cannot hang.
 */
export function useDetailsTimeline(
	therapistId: string,
	data?: DetailsTimeline | null,
): DetailsTimelineState {
	const [failedFor, setFailedFor] = useState<string | null>(null);

	const retry = useCallback(() => {
		setFailedFor(null);
		let answered = false;
		router.reload({
			only: ["selectedTherapistTimeline"],
			onSuccess: () => {
				answered = true;
			},
			onFinish: () => {
				if (!answered) setFailedFor(therapistId);
			},
		});
	}, [therapistId]);

	// one request per therapist opened
	useEffect(() => {
		retry();
	}, [retry]);

	// the prop outlives the panel, so another therapist's answer counts as not loaded yet
	if (data && String(data.therapistId) === String(therapistId))
		return {
			relocationSupported: data.relocationSupported,
			timeline: data.timeline ?? undefined,
			upcomingVisits: data.upcomingVisits,
			retry,
		};
	return {
		relocationSupported: true,
		timeline:
			failedFor === therapistId ? { data: null, error: null } : undefined,
		upcomingVisits: [],
		retry,
	};
}

/**
 * The details panel's Alamat: the edit page's garis waktu and Tangga without its actions, then the
 * Alamat Lain. The timeline comes through useDetailsTimeline, so the rest of the details never wait on Go.
 */
export function DetailsAddressSection({
	addresses,
	timeline,
}: {
	addresses: TherapistAddress[];
	timeline: DetailsTimelineState;
}) {
	const { t } = useTranslation("therapists", { keyPrefix: "form.address" });
	// Rails' relocation_chain mirrors Go's, so these never appear in the Tangga
	const others = addresses.filter(
		(row) => !row.active && !row.managedByRelocation,
	);

	return (
		<div className="grid gap-4">
			<AddressTimelineBlock
				relocationSupported={timeline.relocationSupported}
				timeline={timeline.timeline}
				addresses={addresses}
				upcomingVisits={timeline.upcomingVisits}
				onRetry={timeline.retry}
			/>
			{others.length > 0 && (
				<div className="grid">
					<p className="text-xs font-semibold tracking-wide uppercase text-muted-foreground">
						{t("others.title")} · {others.length}
					</p>
					{others.map((row) => (
						<div
							key={row.id}
							className="grid gap-0.5 py-3 border-b last:border-b-0"
						>
							<AddressLines
								tag={t("others.title")}
								tagClassName="border-border text-muted-foreground"
								city={row.location.city}
								province={row.location.state}
								address={row.address}
								postalCode={row.postalCode}
								latitude={row.latitude}
								longitude={row.longitude}
							/>
						</div>
					))}
				</div>
			)}
		</div>
	);
}
